/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PhoneNumbers } from '../../../models/ProviderDetails';
import { DirectoryPhoneNumbers } from './DirectoryPhoneNumbers';
import { ShowAllButton, ShowAllText } from './DirectoryPhoneNumbers.styled';

const phoneDrawerStyles = {
  'abyss-modal-content-title': {
    fontFamily: '$primary !important',
    fontSize: '28.83px !important',
    lineHeight: '24px !important',
    fontWeight: '700 !important',
    letterSpacing: '0px !important',
    color: '$primary1 !important',
  },

  'abyss-modal-content-container': {
    height: 'auto !important',
  },
  'abyss-modal-body': {
    color: '$gray8',
    whiteSpace: 'pre-line',
  },
  'abyss-modal-close-button': {
    color: '#dbcbcb !important',
    margin: '8px 10px 0 0',
  },
  'abyss-modal-header-container': {
    margin: '0px 0px 16px 0px',
  },
};

type Props = {
  phoneNumbers?: PhoneNumbers | null;
  tty?: string;
  isFromVirtualProviderGroup?: boolean;
  showAdditionalPhones?: boolean;
  showExamPhone?: boolean;
  isCNSNJPlanMember?: boolean;
  enableAfterHoursPhone?: boolean;
  mobile?: boolean;
};

type PhoneCounts = {
  phoneCount: number;
  appointmentCount: number;
  emergencyCount: number;
  afterHoursCount: number;
  faxCount: number;
  ttyCount: number;
};

function getPhoneCounts(
  phoneNumbers: PhoneNumbers,
  tty: string | undefined
): PhoneCounts {
  return {
    phoneCount: phoneNumbers.phone?.length || 0,
    appointmentCount: phoneNumbers.appointment?.length || 0,
    emergencyCount: phoneNumbers.emergency?.length || 0,
    afterHoursCount: phoneNumbers.afterHours?.length || 0,
    faxCount: phoneNumbers.fax?.length || 0,
    ttyCount: tty ? 1 : 0,
  };
}

function calculateEffectiveAfterHoursCount(
  afterHoursCount: number,
  phoneCount: number,
  enableAfterHoursPhone: boolean
): number {
  if (!enableAfterHoursPhone) {
    return 0;
  }
  if (afterHoursCount > 0) {
    return afterHoursCount;
  }
  return phoneCount > 0 ? 1 : 0;
}

function calculateDesktopAdditionalCount(
  counts: PhoneCounts,
  options: {
    showAdditionalPhones: boolean;
    showExamPhone: boolean;
    isCNSNJPlanMember: boolean;
    isFromVirtualProviderGroup: boolean;
  },
  effectiveAfterHoursCount: number
): number {
  const emergencyEntryCount =
    (options.showAdditionalPhones ? 1 : 0) + (options.showExamPhone ? 1 : 0);

  const additionalPhones = Math.max(0, counts.phoneCount - 1);
  const additionalAppointment = options.showAdditionalPhones
    ? Math.max(0, counts.appointmentCount - 1)
    : 0;
  const additionalEmergency =
    emergencyEntryCount * Math.max(0, counts.emergencyCount - 1);
  const additionalAfterHours = Math.max(0, effectiveAfterHoursCount - 1);
  const additionalFax = options.isCNSNJPlanMember
    ? Math.max(0, counts.faxCount - 1)
    : 0;

  return (
    additionalPhones +
    additionalAppointment +
    additionalEmergency +
    additionalAfterHours +
    additionalFax
  );
}

function calculateMobileAdditionalCount(
  counts: PhoneCounts,
  options: {
    showAdditionalPhones: boolean;
    showExamPhone: boolean;
    isCNSNJPlanMember: boolean;
    isFromVirtualProviderGroup: boolean;
  },
  effectiveAfterHoursCount: number
): number {
  const emergencyEntryCount =
    (options.showAdditionalPhones ? 1 : 0) + (options.showExamPhone ? 1 : 0);
  const additionalAppointment = options.showAdditionalPhones
    ? Math.max(0, counts.appointmentCount - 1)
    : 0;
  const additionalTty = options.isFromVirtualProviderGroup
    ? 0
    : counts.ttyCount;
  const faxCountToAdd = options.isCNSNJPlanMember ? counts.faxCount : 0;

  return (
    Math.max(0, counts.phoneCount - 1) +
    additionalAppointment +
    emergencyEntryCount * counts.emergencyCount +
    effectiveAfterHoursCount +
    additionalTty +
    faxCountToAdd
  );
}

export function ViewAllPhonesLink({
  phoneNumbers,
  tty,
  isFromVirtualProviderGroup = false,
  showAdditionalPhones = true,
  showExamPhone = false,
  isCNSNJPlanMember = false,
  enableAfterHoursPhone = true,
  mobile = false,
}: Props) {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const drawerTitle = t('PROVIDER_LOCATIONS_DETAILS.ALL_PHONE_NUMBERS');

  if (!phoneNumbers) {
    return null;
  }

  const counts = getPhoneCounts(phoneNumbers, tty);
  const effectiveAfterHoursCount = calculateEffectiveAfterHoursCount(
    counts.afterHoursCount,
    counts.phoneCount,
    enableAfterHoursPhone
  );

  const options = {
    showAdditionalPhones,
    showExamPhone,
    isCNSNJPlanMember,
    isFromVirtualProviderGroup,
  };

  const additionalCount = mobile
    ? calculateMobileAdditionalCount(counts, options, effectiveAfterHoursCount)
    : calculateDesktopAdditionalCount(
        counts,
        options,
        effectiveAfterHoursCount
      );

  if (additionalCount === 0) {
    return null;
  }

  const additionalNumbersTextKey =
    additionalCount === 1
      ? 'PROVIDER_LOCATIONS_DETAILS.VIEW_ADDITIONAL_NUMBER'
      : 'PROVIDER_LOCATIONS_DETAILS.VIEW_ADDITIONAL_NUMBERS';

  return (
    <>
      <ShowAllButton
        data-testid="view-all-phones-link"
        mobile={mobile}
        onClick={() => setIsDrawerOpen(true)}
      >
        <ShowAllText>
          {t(additionalNumbersTextKey, {
            additionalCount,
          })}
        </ShowAllText>
        <IconSymbol color="$core.color.brand.60" icon="chevron_right" />
      </ShowAllButton>
      <Drawer
        css={phoneDrawerStyles}
        data-testid="phone-numbers-drawer"
        header={drawerTitle}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        position="right"
        size={mobile ? 'sm' : 400}
      >
        <div>
          <DirectoryPhoneNumbers
            enableAfterHoursPhone={enableAfterHoursPhone}
            isCNSNJPlanMember={isCNSNJPlanMember}
            isFromVirtualProviderGroup={isFromVirtualProviderGroup}
            phoneNumbers={phoneNumbers}
            showAdditionalPhones={showAdditionalPhones}
            showExamPhone={showExamPhone}
            tty={tty}
          />
        </div>
      </Drawer>
    </>
  );
}
